<template>
    <div class="flex xxs:flex-col xxs:justify-center xxs:items-center lg:flex-row lg:justify-between mt-5">
        <div class="text-body-6 text-interactive-6 min-w-[150px] xxs:text-center lg:text-left">{{ infoLabel }}</div>
        <div class="flex xxs:mt-8 lg:mt-0">
            <div v-for="(link, index) in links" :key="index">
                <div v-if="index === 0"
                     @click="handlePreviousPage"
                     class="select-none text-body-6 rounded w-6 h-6 box-border px-6 py-4 flex justify-center items-center mx-4"
                     :class="{'cursor-not-allowed': lastPage === 1, 'cursor-pointer hover:bg-primary-1 hover:opacity-50': lastPage > 1}"
                >
                    <img src="@/assets/icons/chevron-left.svg" alt=""/>
                </div>
                <div v-if="index > 0 && index < links.length - 1"
                     class="select-none text-body-6 rounded w-6 h-6 box-border px-6 py-4 flex justify-center items-center mx-4"
                     :class="{'bg-interactive-6 text-white': link.active, 'cursor-pointer': link.url !== null && !link.active,
                                'cursor-not-allowed': link.url === null || link.active, 'hover:text-primary-1': link.url !== null && !link.active}"
                     @click="handleOnPageChange(link)"
                >
                    {{ link.label }}
                </div>
                <div
                    v-if="index === links.length - 1"
                    @click="handleNextPage"
                    class="select-none text-body-6 rounded w-6 h-6 box-border px-6 py-4 flex justify-center items-center mx-4"
                    :class="{'cursor-not-allowed': lastPage === 1, 'cursor-pointer hover:bg-primary-1 hover:opacity-50': lastPage > 1}"
                >
                    <img src="@/assets/icons/chevron-right.svg" alt=""/>
                </div>
            </div>
        </div>
        <div
            class="flex text-body-6 text-interactive-6 justify-center items-center select-none xxs:mt-8 lg:mt-0">
            <div class="-mt-px">Per page:</div>
            <Menu as="div" class="relative inline-block text-left ml-2">
                <div>
                    <MenuButton
                        class="flex justify-center items-center mx-2"
                    >
                        {{ perPage }}
                        <img class="ml-1" src="@/assets/icons/chevron-down.svg" alt=""/>
                    </MenuButton>
                </div>

                <transition
                    enter-active-class="transition duration-100 ease-out"
                    enter-from-class="transform scale-95 opacity-0"
                    enter-to-class="transform scale-100 opacity-100"
                    leave-active-class="transition duration-75 ease-in"
                    leave-from-class="transform scale-100 opacity-100"
                    leave-to-class="transform scale-95 opacity-0"
                >
                    <MenuItems

                        class="absolute right-0 mt-2 w-15 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                    >
                        <div class="px-1 py-1">
                            <MenuItem :disabled="total === 0" v-slot="{ active }" v-for="item in perPageOptions" :key="item">
                                <button @click="updatePerPage(item)"
                                        :class="[
                                        active ?? 'bg-primary-1',
                                        'group flex justify-end w-full items-center rounded-md px-4 py-2 text-body-6 hover:text-primary-1',
                                        total === 0 ? 'cursor-not-allowed' : 'cursor-pointer'
                                    ]">
                                    {{ item }}
                                </button>
                            </MenuItem>
                        </div>
                    </MenuItems>
                </transition>
            </Menu>
        </div>
    </div>
</template>

<script setup>
import {Menu, MenuButton, MenuItems, MenuItem} from '@headlessui/vue';
import {computed, ref} from "vue";


const props = defineProps({
    from: {
        type: Number,
        required: false,
        default: 0
    },
    to: {
        type: Number,
        required: false,
        default: 0
    },
    total: {
        type: Number,
        required: true
    },
    lastPage: {
        type: Number,
        required: true
    },
    links: {
        type: Array,
        required: true
    },
    perPageOptions: {
        type: Array,
        required: false,
        default: () => [15, 30, 60, 100]
    },
    initialPage: {
        type: Number,
        required: false,
        default: () => 1
    },
    initialPerPage: {
        type: Number,
        required: false,
        default: () => 15
    },
});

const emit = defineEmits(['change']);

const currentPage = ref(props.initialPage);
const perPage = ref(props.initialPerPage);

const infoLabel = computed(() => {
    return `${props.from} to ${props.to} of ${props.total}`;
});

const handlePreviousPage = () => {
    if (props.lastPage === 1) {
        return;
    }

    if (currentPage.value > 1) {
        currentPage.value--;
    } else {
        currentPage.value = props.lastPage;
    }

    emit('change', {
        page: currentPage.value,
        perPage: perPage.value
    });
}

const handleNextPage = () => {
    if (props.lastPage === 1) {
        return;
    }

    if (currentPage.value === props.lastPage) {
        currentPage.value = 1;
    } else {
        currentPage.value++;
    }

    emit('change', {
        page: currentPage.value,
        perPage: perPage.value
    });
}

const handleOnPageChange = (link) => {
    if (link.url === null || link.active) {
        return;
    }

    currentPage.value = Number.parseInt(link.label);

    emit('change', {
        page: currentPage.value,
        perPage: perPage.value
    });
}

const updatePerPage = (value) => {
    currentPage.value = 1;
    perPage.value = value;

    emit('change', {
        page: currentPage.value,
        perPage: perPage.value
    });
}

</script>

